import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Role, COLLECTION_NAMES } from '../types';
import { useAuth } from '../../../contexts/AuthContext';

export const useAccountabilityChart = () => {
  const { currentUser } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for roles (matching legacy subscribeToRoles behavior)
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Query roles ordered by level and position (sortOrder)
    const rolesQuery = query(
      collection(db, COLLECTION_NAMES.ACCOUNTABILITY_ROLES),
      orderBy('level', 'asc'),
      orderBy('sortOrder', 'asc')
    );

    const unsubscribe = onSnapshot(
      rolesQuery,
      (snapshot) => {
        const rolesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as Role;
        });
        setRoles(rolesData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching roles:', err);
        setError(err.message || 'Failed to fetch accountability chart.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const saveRole = async (
    roleData: Partial<Role>,
    roleId?: string,
    parentRole?: Role | null
  ): Promise<string> => {
    if (!currentUser) throw new Error('User not authenticated.');

    // Clean responsibilities (filter out empty strings)
    const cleanedResponsibilities = (roleData.responsibilities || []).filter(
      (r) => r.trim() !== ''
    );

    const rolesColRef = collection(db, COLLECTION_NAMES.ACCOUNTABILITY_ROLES);

    if (roleId) {
      // Update existing role (matching legacy handleSaveRole for editing)
      const roleRef = doc(db, COLLECTION_NAMES.ACCOUNTABILITY_ROLES, roleId);
      await updateDoc(roleRef, {
        title: roleData.title,
        ownerName: roleData.ownerName || null,
        subtitle: roleData.subtitle || null,
        responsibilities: cleanedResponsibilities,
        updatedAt: serverTimestamp(),
      });
      return roleId;
    } else {
      // Create new role (matching legacy handleSaveRole for adding)
      // Calculate position (sortOrder) - find max position for siblings
      let maxPosition = -1;
      if (parentRole) {
        const siblings = roles.filter((r) => r.parentId === parentRole.id);
        maxPosition = siblings.reduce(
          (max, r) => Math.max(max, r.sortOrder),
          -1
        );
      } else {
        // Root level - find max position at level 0
        const rootRoles = roles.filter((r) => r.level === 0);
        maxPosition = rootRoles.reduce(
          (max, r) => Math.max(max, r.sortOrder),
          -1
        );
      }

      const newRoleData = {
        title: roleData.title || '',
        ownerName: roleData.ownerName || null,
        subtitle: roleData.subtitle || null,
        responsibilities: cleanedResponsibilities,
        parentId: parentRole?.id || null,
        level: parentRole ? parentRole.level + 1 : 0,
        sortOrder: maxPosition + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const newRoleRef = await addDoc(rolesColRef, newRoleData);
      return newRoleRef.id;
    }
  };

  const deleteRole = async (roleId: string): Promise<void> => {
    if (!currentUser) throw new Error('User not authenticated.');
    
    // This implementation replicates the source's behavior of deleting a role and all its children.
    // We find children client-side and batch delete them (matching legacy handleDeleteRole).

    try {
      const batch = writeBatch(db);
      
      const childrenIdsToDelete: string[] = [];
      const findChildrenRecursive = (parentId: string) => {
        roles.forEach((role) => {
          if (role.parentId === parentId) {
            childrenIdsToDelete.push(role.id);
            findChildrenRecursive(role.id); // Recurse
          }
        });
      };

      // Find all children and add to delete list
      findChildrenRecursive(roleId);

      // Add the children to the batch delete
      childrenIdsToDelete.forEach((id) => {
        batch.delete(doc(db, COLLECTION_NAMES.ACCOUNTABILITY_ROLES, id));
      });

      // Add the main role to the batch delete
      batch.delete(doc(db, COLLECTION_NAMES.ACCOUNTABILITY_ROLES, roleId));

      await batch.commit();
      // Real-time listener will automatically update the UI
    } catch (err) {
      console.error('Error deleting role:', err);
      setError('Failed to delete role.');
      throw err;
    }
  };

  // Helper functions matching legacy code
  const getRolesByLevel = (level: number): Role[] => {
    return roles
      .filter((r) => r.level === level)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const getChildRoles = (parentId: string): Role[] => {
    return roles
      .filter((r) => r.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };

  return {
    roles,
    loading,
    error,
    saveRole,
    deleteRole,
    getRolesByLevel,
    getChildRoles,
  };
};

